import * as React from "react";
import { Eye, EyeOff } from "lucide-react";
import { Text } from "../../../ui/components/Text";
import { Callout } from "../../../ui/components/Callout";
import { ListView } from "../../../ui/components/ListView";
import { useAuth } from "../../lib/auth/hooks";
import { useWebSocket } from "../../hooks/useWebSocket";
import { useI18n } from "../../lib/i18n";
import * as systemEventsApi from "../../lib/api/system-events";
import type { SystemEvent } from "../../lib/api/system-events";
import type { ListParams } from "../../../ui/api/listApi.types";
import { createSystemEventsTableColumns } from "./systemEventsTableColumns";
import { SystemEventModal } from "./SystemEventModal";
import * as pageStyles from "../users/UsersListPage.css";

function asStringArray(value: unknown): string[] {
  if (Array.isArray(value)) {
    return value.map(String).filter(Boolean);
  }
  if (typeof value === "string" && value.trim()) {
    return [value.trim()];
  }
  return [];
}

function sortEvents(
  rows: SystemEvent[],
  sortBy: string | undefined,
  order: "asc" | "desc" | undefined,
): SystemEvent[] {
  const dir = order === "desc" ? -1 : 1;
  const key = (sortBy || "label") as keyof SystemEvent;
  return [...rows].sort((a, b) => {
    const av = String(a[key] ?? "").toLowerCase();
    const bv = String(b[key] ?? "").toLowerCase();
    if (av < bv) return -1 * dir;
    if (av > bv) return 1 * dir;
    return 0;
  });
}

export function SystemEventsListPage() {
  const { accessToken, user } = useAuth();
  const { t } = useI18n();
  const { subscribe, unsubscribe } = useWebSocket();
  const [refreshKey, setRefreshKey] = React.useState(0);
  const [modalEventId, setModalEventId] = React.useState<string | null>(null);
  const [loadError, setLoadError] = React.useState<string | null>(null);
  const [includeRetired, setIncludeRetired] = React.useState(false);

  const refresh = React.useCallback(() => setRefreshKey((k) => k + 1), []);
  const token =
    accessToken ??
    (typeof localStorage !== "undefined" ? localStorage.getItem("access_token") : null);

  React.useEffect(() => {
    const handler = () => refresh();
    subscribe(["entity:system-events:global"], handler);
    return () => unsubscribe(["entity:system-events:global"], handler);
  }, [subscribe, unsubscribe, refresh]);

  const columns = React.useMemo(() => createSystemEventsTableColumns(t), [t]);

  const headerActions = React.useMemo(
    () => [
      {
        id: "toggle-retired",
        label: includeRetired
          ? t("pro.system.events.hideRetired")
          : t("pro.system.events.showRetired"),
        icon: includeRetired ? <EyeOff size={14} /> : <Eye size={14} />,
        color: "neutral" as const,
        variant: "outline" as const,
        onClick: () => setIncludeRetired((v) => !v),
      },
    ],
    [includeRetired, t],
  );

  const fetchEvents = React.useCallback(
    async (params: ListParams) => {
      if (!token) return { data: [] as SystemEvent[], total: 0 };
      try {
        const events = await systemEventsApi.fetchSystemEvents(token, {
          includeRetired,
          search: params.search,
        });
        setLoadError(null);

        const domains = asStringArray(params.filters?.domain);
        const sources = asStringArray(params.filters?.sourceService);
        let filtered = events;
        if (domains.length > 0) {
          const set = new Set(domains);
          filtered = filtered.filter((e) => set.has(e.domain));
        }
        if (sources.length > 0) {
          const set = new Set(sources);
          filtered = filtered.filter((e) => set.has(String(e.sourceService)));
        }

        const sorted = sortEvents(filtered, params.sort_by, params.order);
        const page = Math.max(1, params.page || 1);
        const limit = Math.max(1, params.limit || 25);
        const start = (page - 1) * limit;
        return {
          data: sorted.slice(start, start + limit),
          total: sorted.length,
        };
      } catch (err: any) {
        setLoadError(err?.message || t("pro.system.events.error.load"));
        return { data: [], total: 0 };
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [token, refreshKey, includeRetired, t],
  );

  if (!token) {
    return (
      <div className={pageStyles.page}>
        <Callout variant="error">{t("pro.system.events.signInRequired")}</Callout>
      </div>
    );
  }

  return (
    <div className={pageStyles.page}>
      <div className={pageStyles.pageHeader}>
        <Text level="h1" color="text" weight="bold">
          {t("pro.system.events.title")}
        </Text>
      </div>

      {loadError && (
        <div className={pageStyles.errorWrap}>
          <Callout variant="error">{loadError}</Callout>
        </div>
      )}

      <div className={pageStyles.tableHost}>
        <ListView<SystemEvent>
          listId="system-events"
          userId={user?.id ?? "anonymous"}
          columns={columns}
          fetchList={fetchEvents}
          contextKey={`${refreshKey}:${includeRetired}`}
          fillContainer
          enabledViews={["table"]}
          searchPlaceholder={t("pro.system.events.search")}
          emptyTitle={t("pro.system.events.emptyTitle")}
          emptySubtitle={t("pro.system.events.emptySubtitle")}
          defaultSort={{ sort_by: "label", order: "asc" }}
          onRowClick={(row) => setModalEventId(row.id)}
          headerActions={headerActions}
        />
      </div>

      {modalEventId !== null && (
        <SystemEventModal
          eventId={modalEventId}
          open={modalEventId !== null}
          setOpen={(open) => {
            if (!open) setModalEventId(null);
          }}
          onSaved={() => {
            setModalEventId(null);
            refresh();
          }}
        />
      )}
    </div>
  );
}
